'use client'

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { Arrow } from '@/components'

import styles from './slider.module.css'

interface SlidersProps {
  items: ReactNode[]
  gap?: number
}

const Sliders = ({ items, gap = 16 }: SlidersProps) => {
  const [scrollWidth, setScrollWidth] = useState<Array<number>>([])
  const [current, setCurrent] = useState(0)
  const scrollRef = useRef<HTMLDivElement>(null)

  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const scrollLeft = el.scrollLeft
    const maxScrollLeft = el.scrollWidth - el.clientWidth

    // Find the nearest scroll position in the scrollWidth array
    let closestIndex = 0
    let minDistance = Number.MAX_VALUE

    scrollWidth.forEach((position, index) => {
      const distance = Math.abs(position - scrollLeft)

      if (distance < minDistance) {
        minDistance = distance
        closestIndex = index
      }
    })

    if (minDistance < 10) {
      setCurrent(closestIndex)
    }

    setCanScrollLeft(scrollLeft > 0)
    setCanScrollRight(scrollLeft < maxScrollLeft - 1)
  }, [scrollWidth])

  const scrollLeft = useCallback(() => {
    const cur = scrollRef.current?.children[current - 1]
    const el = scrollRef.current

    if (!el) return

    if (cur) {
      const targetIndex = current - 1
      setCurrent(targetIndex)
      el.scrollTo({ left: scrollWidth[targetIndex], behavior: 'smooth' })
    }
  }, [current, scrollWidth])

  const scrollRight = useCallback(() => {
    const cur = scrollRef.current?.children[current + 1]
    const el = scrollRef.current
    if (!el) return

    if (cur) {
      const targetIndex = current + 1
      setCurrent(targetIndex)
      el.scrollTo({ left: scrollWidth[targetIndex], behavior: 'smooth' })
    }
  }, [current, scrollWidth])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    checkScroll()

    let isScrolling: NodeJS.Timeout

    function handleScroll() {
      window.clearTimeout(isScrolling)

      isScrolling = setTimeout(() => {
        checkScroll()
      }, 150)
    }

    el.addEventListener('scroll', handleScroll)
    return () => el.removeEventListener('scroll', handleScroll)
  }, [checkScroll])

  useEffect(() => {
    const scrollWis: Array<number> = [0]
    let accumulatedWidth = 0

    if (scrollRef.current) {
      items?.forEach((_item, index) => {
        // Get the actual width of each item element including any margins
        const itemElement = scrollRef.current?.children[index] as HTMLElement
        if (itemElement) {
          const itemWidth = itemElement.offsetWidth

          // Add the gap only after the first item
          if (index > 0) {
            accumulatedWidth += gap
          }

          accumulatedWidth += itemWidth
          scrollWis.push(accumulatedWidth)
        }
      })

      setScrollWidth(scrollWis)
    }
  }, [items, gap])

  return (
    <>
      <div className={styles.main} data-core-scroller>
        <div
          className={`${styles.scroll} horizontal-scrollable-container`}
          ref={scrollRef}
          data-core-scroller-platter
          role="list">
          {items?.map((item, index) => (
            <div key={index} className={styles.item} data-core-scroller-item role="listitem">
              <div className={styles.inner}>{item}</div>
              {index == items.length - 1 ? <div className={styles.empty}></div> : null}
            </div>
          ))}
        </div>
        <div className={styles.action}>
          {canScrollLeft ? (
            <div className="w-[144px] text-white group-hover:lg:block">
              <div className="flex h-full items-center justify-center">
                <button
                  className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-black/40 transition-colors duration-200 hover:bg-black/30 focus:bg-black/65 focus:outline-none active:bg-black/65"
                  onClick={scrollLeft}
                  onMouseLeave={(e) => e.currentTarget.blur()}>
                  <Arrow width={20} height={20} rotate={180} color="currentColor" />
                </button>
              </div>
            </div>
          ) : (
            <div></div>
          )}
          {canScrollRight ? (
            <div className="w-[144px] text-white group-hover:lg:block">
              <div className="flex h-full items-center justify-center">
                <button
                  className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-black/40 transition-colors duration-200 hover:bg-black/30 focus:bg-black/65 focus:outline-none active:bg-black/65"
                  onClick={scrollRight}
                  onMouseLeave={(e) => e.currentTarget.blur()}>
                  <Arrow width={20} height={20} color="currentColor" />
                </button>
              </div>
            </div>
          ) : (
            <div></div>
          )}
        </div>
      </div>
    </>
  )
}

export default Sliders
